import z from "zod/v4";
import type { Strategy } from "./strategy-types";
import { StrategyBaseSchema_StrategyChildrenNotTypeChecked } from "./zod";

export const LEGACY_TOKEN_STRATEGY_VERSION = "1.0.0" as const;
export const CANONICAL_ASSET_STRATEGY_VERSION = "2.0.0" as const;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isCanonicalAssetId = (value: string): boolean =>
  UUID_PATTERN.test(value);

export type StrategyAssetVocabulary = "none" | "token" | "canonical" | "mixed";

type AssetVocabularyInspection = {
  readonly vocabulary: StrategyAssetVocabulary;
  readonly canonicalAssetIds: readonly string[];
  readonly invalidCanonicalAssetIds: readonly string[];
  readonly malformedAssetBlockPaths: readonly string[];
  readonly tokenAssetIdPaths: readonly string[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Inspects only the asset-reference vocabulary. Full recursive strategy
 * semantics remain the evaluator/validator's responsibility, matching the
 * legacy reader boundary.
 */
export const inspectStrategyAssetVocabulary = (
  value: unknown,
): AssetVocabularyInspection => {
  let hasToken = false;
  let hasCanonical = false;
  const canonicalAssetIds = new Set<string>();
  const invalidCanonicalAssetIds = new Set<string>();
  const malformedAssetBlockPaths: string[] = [];
  const tokenAssetIdPaths = new Set<string>();

  const isTokenAssetIdField = (key: string): boolean =>
    (key === "assetId" || key.endsWith("AssetId")) &&
    !key.toLowerCase().includes("canonical");

  const visit = (node: unknown, path: string): void => {
    if (Array.isArray(node)) {
      node.forEach((entry, index) => visit(entry, `${path}[${index}]`));
      return;
    }
    if (!isRecord(node)) return;

    if (node.blockType === "asset") {
      const hasTokenRef = typeof node.assetId === "string";
      const hasCanonicalRef = typeof node.assetCanonicalId === "string";
      if (hasTokenRef === hasCanonicalRef) {
        malformedAssetBlockPaths.push(path);
      }
      if (hasCanonicalRef) {
        hasCanonical = true;
        const canonicalId = node.assetCanonicalId as string;
        if (isCanonicalAssetId(canonicalId)) {
          canonicalAssetIds.add(canonicalId);
        } else {
          invalidCanonicalAssetIds.add(canonicalId);
        }
      }
    }

    for (const [key, entry] of Object.entries(node)) {
      const entryPath = `${path}.${key}`;
      if (isTokenAssetIdField(key) && entry !== undefined) {
        hasToken = true;
        tokenAssetIdPaths.add(entryPath);
      }
      if (key !== "blockIdForInstance" && key !== "parentNodeId") {
        visit(entry, entryPath);
      }
    }
  };

  if (isRecord(value) && "entry" in value) visit(value.entry, "entry");
  const vocabulary: StrategyAssetVocabulary =
    hasToken && hasCanonical
      ? "mixed"
      : hasCanonical
        ? "canonical"
        : hasToken
          ? "token"
          : "none";
  return {
    vocabulary,
    canonicalAssetIds: [...canonicalAssetIds].sort(),
    invalidCanonicalAssetIds: [...invalidCanonicalAssetIds].sort(),
    malformedAssetBlockPaths,
    tokenAssetIdPaths: [...tokenAssetIdPaths].sort(),
  };
};

export const LEGACY_CANONICAL_STRATEGY_UNSUPPORTED_ERROR_CODE =
  "legacy_canonical_strategy_unsupported" as const;

/**
 * Typed cutover guard for legacy scheduling/evaluation paths. Canonical
 * strategies require execution-time issuance resolution and therefore must
 * never enter the token-only apps/engine pipeline.
 */
export class LegacyCanonicalStrategyUnsupportedError extends Error {
  readonly code = LEGACY_CANONICAL_STRATEGY_UNSUPPORTED_ERROR_CODE;

  constructor() {
    super(
      "Canonical strategy documents are unsupported by the legacy scheduling pipeline",
    );
    this.name = "LegacyCanonicalStrategyUnsupportedError";
  }
}

export const assertLegacyTokenStrategyDocument = (value: unknown): void => {
  const inspection = inspectStrategyAssetVocabulary(value);
  if (
    inspection.canonicalAssetIds.length > 0 ||
    inspection.invalidCanonicalAssetIds.length > 0 ||
    (isRecord(value) && value.version === CANONICAL_ASSET_STRATEGY_VERSION)
  ) {
    throw new LegacyCanonicalStrategyUnsupportedError();
  }
};

const DirectIndexUniverseRefV2Schema = z.object({
  kind: z.enum(["index", "sector"]),
  id: z.string(),
  symbol: z.string().optional(),
  name: z.string(),
});

const DirectIndexExclusionV2Schema = z.object({
  assetCanonicalId: z.string().refine(isCanonicalAssetId),
  ticker: z.string(),
});

const DirectIndexPositionV2Schema = z
  .object({
    ticker: z.string(),
    assetCanonicalId: z.string().refine(isCanonicalAssetId),
    weightPct: z.string(),
    // A known-never key rejects instead of being stripped by passthrough.
    assetId: z.never().optional(),
  })
  .passthrough();

/** Runtime contract for canonical direct-index provenance. */
export const DirectIndexProvenanceV2Schema = z
  .object({
    v: z.literal(2),
    universe: DirectIndexUniverseRefV2Schema,
    topN: z.number().int().positive(),
    weighting: z.enum(["index", "market-cap", "equal"]),
    rebalancing: z.enum(["daily", "weekly", "monthly"]),
    excludes: z.array(DirectIndexExclusionV2Schema),
    positions: z.array(DirectIndexPositionV2Schema).min(1),
    snapshotAt: z.string(),
    // Canonical provenance cannot carry a builder-selected execution chain.
    resolvedChainId: z.never().optional(),
  })
  .passthrough();

/**
 * Versioned compatibility reader. Legacy token documents retain the exact
 * permissive v1 boundary; only canonical asset blocks opt into v2.
 */
export const StrategyDocumentSchema =
  StrategyBaseSchema_StrategyChildrenNotTypeChecked.superRefine(
    (document, context) => {
      const inspection = inspectStrategyAssetVocabulary(document);
      for (const path of inspection.malformedAssetBlockPaths) {
        context.addIssue({
          code: "custom",
          message:
            "Asset blocks must contain exactly one of assetId or assetCanonicalId",
          path: [path],
        });
      }
      for (const canonicalId of inspection.invalidCanonicalAssetIds) {
        context.addIssue({
          code: "custom",
          message: `assetCanonicalId must be a UUID: ${canonicalId}`,
          path: ["entry"],
        });
      }
      if (inspection.vocabulary === "mixed") {
        context.addIssue({
          code: "custom",
          message:
            "Token and canonical asset blocks cannot be mixed in one strategy document",
          path: ["entry"],
        });
      }
      if (
        inspection.vocabulary === "canonical" &&
        document.version !== CANONICAL_ASSET_STRATEGY_VERSION
      ) {
        context.addIssue({
          code: "custom",
          message: `Canonical asset blocks require strategy version ${CANONICAL_ASSET_STRATEGY_VERSION}`,
          path: ["version"],
        });
      }
      if (
        document.version === CANONICAL_ASSET_STRATEGY_VERSION &&
        inspection.vocabulary !== "canonical"
      ) {
        context.addIssue({
          code: "custom",
          message: `Strategy version ${CANONICAL_ASSET_STRATEGY_VERSION} is reserved for canonical asset documents`,
          path: ["version"],
        });
      }
      if (
        document.version === CANONICAL_ASSET_STRATEGY_VERSION &&
        document.directIndex !== undefined
      ) {
        const provenance = DirectIndexProvenanceV2Schema.safeParse(
          document.directIndex,
        );
        if (!provenance.success) {
          for (const issue of provenance.error.issues) {
            context.addIssue({
              code: "custom",
              message: issue.message,
              path: ["directIndex", ...issue.path],
            });
          }
        }
      }
    },
  );

export const parseStrategyDocument = (value: unknown): Strategy => {
  StrategyDocumentSchema.parse(value);
  // The schema validates the document boundary while recursive semantic
  // validation remains in validateStrategy/evaluateStrategy by design. Return
  // the validated input itself so legacy JSON serialization stays byte-stable.
  return value as Strategy;
};

export const safeParseStrategyDocument = (
  value: unknown,
):
  | { readonly success: true; readonly data: Strategy }
  | { readonly success: false; readonly error: z.ZodError } => {
  const parsed = StrategyDocumentSchema.safeParse(value);
  return parsed.success
    ? {
        // Safe because the schema just validated the same input. Preserve the
        // original key ordering for the legacy compatibility reader.
        success: true,
        data: value as Strategy,
      }
    : { success: false, error: parsed.error };
};
